import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Bus, CreditCard, Copy, Check, ChevronDown,
} from "lucide-react";
import WeddingPreview from "@/components/WeddingPreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Bloque visual Autobús ────────────────────────────────────────────────────
function BusBlock({ departure, returnTimes }) {
    if (!departure && !returnTimes) return null;
    return (
        <div
            className="mx-4 my-6 rounded-2xl border border-[var(--line)] bg-[var(--bg-soft)] px-5 py-4"
            style={{ background: "rgba(0,0,0,0.02)" }}
        >
            <div className="flex items-center gap-2 mb-3">
                <span
                    className="flex items-center justify-center w-8 h-8 rounded-full"
                    style={{ background: "var(--ink)", color: "#fff" }}
                >
                    <Bus size={15} />
                </span>
                <span className="font-serif text-base leading-none">
                    Autobús
                </span>
            </div>
            {departure && (
                <div className="mb-2">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-0.5">
                        Salida y horario de ida
                    </p>
                    <p className="text-sm leading-snug">{departure}</p>
                </div>
            )}
            {returnTimes && (
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-0.5">
                        Horarios de vuelta
                    </p>
                    <p className="text-sm leading-snug">{returnTimes}</p>
                </div>
            )}
        </div>
    );
}

// ─── Botón IBAN oculto ────────────────────────────────────────────────────────
function IbanReveal({ iban }) {
    const [revealed, setReveal] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!iban) return null;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(iban);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Clipboard API can fail in insecure contexts or denied permissions.
            console.warn("IBAN copy failed:", err);
        }
    };

    return (
        <div className="mx-4 my-4">
            {!revealed ? (
                <button
                    onClick={() => setReveal(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-[var(--line)] text-sm text-[var(--ink-soft)] hover:bg-[var(--bg-soft)] transition-colors duration-150"
                    style={{ background: "transparent" }}
                >
                    <CreditCard size={14} className="opacity-60" />
                    Consultar cuenta bancaria para detalles de boda
                    <ChevronDown size={14} className="opacity-50" />
                </button>
            ) : (
                <div
                    className="rounded-xl border border-[var(--line)] px-4 py-3 flex items-center justify-between gap-3"
                    style={{ background: "var(--bg-soft, #faf9f7)" }}
                >
                    <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-0.5">
                            IBAN
                        </p>
                        <p className="font-mono text-sm tracking-wider break-all leading-snug">
                            {iban}
                        </p>
                    </div>
                    <button
                        onClick={handleCopy}
                        className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-150"
                        style={{
                            background: copied
                                ? "var(--ink)"
                                : "var(--line, #ebe8e2)",
                            color: copied ? "#fff" : "var(--ink)",
                        }}
                        aria-label="Copiar IBAN"
                    >
                        {copied ? (
                            <>
                                <Check size={13} />
                                Copiado
                            </>
                        ) : (
                            <>
                                <Copy size={13} />
                                Copiar
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── PublicWedding ────────────────────────────────────────────────────────────
export default function PublicWedding() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const res = await axios.get(`${API}/weddings/by-slug/${slug}`);
                if (!cancelled) setData(res.data);
            } catch (e) {
                console.error("public wedding fetch failed", e);
                if (!cancelled) setError("No encontrada");
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (error) {
        return (
            <div className="h-[100dvh] w-full flex items-center justify-center text-[var(--ink-soft)]">
                <div className="text-center">
                    <div className="font-serif text-3xl mb-2">404</div>
                    <div className="text-sm">Web no encontrada</div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="h-[100dvh] w-full flex items-center justify-center text-[var(--ink-soft)]">
                <div className="text-sm">Cargando...</div>
            </div>
        );
    }

    const busEnabled = data.busEnabled ?? false;
    const ibanEnabled = (data.ibanEnabled ?? false) && !!data.iban;

    return (
        <div className="min-h-[100dvh] w-full max-w-[480px] mx-auto bg-[var(--bg)]">
            {/* Plantilla principal de la boda (incluye el formulario RSVP) */}
            <WeddingPreview data={data} scope="public" />

            {/* Bloques opcionales debajo del WeddingPreview */}
            {busEnabled && (
                <BusBlock
                    departure={data.busDeparture}
                    returnTimes={data.busReturn}
                />
            )}
            {data.iban && ibanEnabled && <IbanReveal iban={data.iban} />}

            <div className="py-10 text-center flex flex-col items-center gap-2">
                <img
                    src="/assets/logo.png"
                    alt="Loving Angels"
                    style={{ height: 14, width: "auto", opacity: 0.35 }}
                />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-soft)] opacity-40">
                    Creado con Loving Angels
                </p>
            </div>
        </div>
    );
}
